import { instance } from "../axios/instance";
import { PATH } from "../shared/constants";
import {
  sanitizeAnalyticsPopupConfig,
  type AnalyticsPopupConfig,
} from "./analyticsPopupConfig";
import { applyHostnameSearchVisibility } from "./searchVisibility";

const GA_MEASUREMENT_ID = "G-CNJ22J348V";
const GTM_ID = "GTM-N679P3F";
const CLIENT_KEY_STORAGE = "momento_analytics_client_key";

export type AnalyticsGate = "unknown" | "allowed" | "denied";

export interface StoredAnalyticsConsent {
  teamId: string;
  policyVersion: string;
  analytics: boolean;
  status: "accepted" | "rejected";
  timestamp: string;
}

let gate: AnalyticsGate = "unknown";
let gtmLoaded = false;
let readProfileId: () => string = () => "";
const waiters: Array<(next: AnalyticsGate) => void> = [];

export function setAnalyticsProfileIdReader(reader: () => string): void {
  readProfileId = reader;
}

function hostPrefix(): string {
  if (typeof window === "undefined") return "";
  return window.location.hostname.split(".")[0].toLowerCase();
}

function storageKey(): string {
  return `momento_analytics_consent_${hostPrefix()}`;
}

function cookieName(): string {
  return `momento_analytics_consent_${hostPrefix()}`;
}

export function analyticsClientKey(): string {
  try {
    const existing = localStorage.getItem(CLIENT_KEY_STORAGE);
    if (existing && /^[a-zA-Z0-9_-]{16,80}$/.test(existing)) return existing;
    const next = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, "")
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(CLIENT_KEY_STORAGE, next);
    return next;
  } catch {
    return `anon${Date.now().toString(36)}`;
  }
}

export function readStoredAnalyticsConsent(): StoredAnalyticsConsent | null {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAnalyticsConsent;
    if (!parsed || (parsed.status !== "accepted" && parsed.status !== "rejected")) return null;
    if (!parsed.teamId || !parsed.policyVersion) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsentCookie(value: StoredAnalyticsConsent | null): void {
  const name = cookieName();
  if (!value) {
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
    return;
  }
  const encoded = encodeURIComponent(JSON.stringify(value));
  const maxAge = 60 * 60 * 24 * 180;
  document.cookie = `${name}=${encoded}; Max-Age=${maxAge}; path=/; SameSite=Lax`;
}

export function consentMatchesTeam(
  stored: StoredAnalyticsConsent | null,
  teamId: string,
  policyVersion: string,
): boolean {
  if (!stored || !teamId) return false;
  return stored.teamId === teamId && stored.policyVersion === policyVersion;
}

function clearGaCookies(): void {
  const names = document.cookie.split(";").map((part) => part.split("=")[0].trim());
  names.forEach((name) => {
    const isGa = name === "_ga" || name === "_gid" || name === "_gat" || name.startsWith("_ga_");
    if (!isGa) return;
    document.cookie = `${name}=; Max-Age=0; path=/`;
    document.cookie = `${name}=; Max-Age=0; path=/; domain=${window.location.hostname}`;
  });
}

function loadGtm(): void {
  if (gtmLoaded || typeof document === "undefined") return;
  gtmLoaded = true;
  const w = window as Window & { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
}

function disableGa(): void {
  (window as unknown as Record<string, boolean>)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  const w = window as Window & { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push([
    "consent",
    "update",
    {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    },
  ]);
  clearGaCookies();
}

export function setAnalyticsGate(next: AnalyticsGate): void {
  gate = next;
  if (next === "allowed") {
    (window as unknown as Record<string, boolean>)[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
    const w = window as Window & { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push([
      "consent",
      "default",
      {
        analytics_storage: "granted",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      },
    ]);
    loadGtm();
  }
  if (next === "denied") disableGa();
  const pending = waiters.splice(0, waiters.length);
  pending.forEach((waiter) => waiter(next));
}

export function analyticsTrackingAllowed(): Promise<boolean> {
  if (gate === "allowed") return Promise.resolve(true);
  if (gate === "denied") return Promise.resolve(false);
  return new Promise((resolve) => {
    waiters.push((next) => resolve(next === "allowed"));
  });
}

export function analyticsImageCountsAllowed(): boolean {
  return gate === "allowed";
}

export async function syncAnalyticsConsent(
  choice: StoredAnalyticsConsent,
  customerProfileId?: string,
): Promise<void> {
  const profile = (customerProfileId || readProfileId() || "").trim();
  await instance.put(`${PATH.API}/${PATH.USER}/analytics-consent`, {
    teamId: choice.teamId,
    clientKey: analyticsClientKey(),
    status: choice.status,
    policyVersion: choice.policyVersion,
    ...(profile ? { customerProfileId: profile } : {}),
  });
}

export function saveAnalyticsChoice(
  teamId: string,
  policyVersion: string,
  status: "accepted" | "rejected",
): StoredAnalyticsConsent {
  const choice: StoredAnalyticsConsent = {
    teamId,
    policyVersion,
    analytics: status === "accepted",
    status,
    timestamp: new Date().toISOString(),
  };
  try {
    localStorage.setItem(storageKey(), JSON.stringify(choice));
  } catch {
    /* private mode */
  }
  try {
    writeConsentCookie(choice);
  } catch {
    /* ignore */
  }
  setAnalyticsGate(status === "accepted" ? "allowed" : "denied");
  syncAnalyticsConsent(choice).catch(() => undefined);
  return choice;
}

export async function bootstrapAnalyticsConsent(): Promise<void> {
  const prefix = hostPrefix();
  if (!prefix) {
    setAnalyticsGate("allowed");
    return;
  }
  try {
    const response = await instance.get(
      `${PATH.API}/${PATH.TEAM}/hostname/${encodeURIComponent(prefix)}/welcome`,
      { timeout: 25_000 },
    );
    const data = response.data as {
      team?: { _id?: string; analyticsTrackingEnabled?: boolean; analyticsPopupConfig?: unknown };
      standardDefault?: boolean;
      notFound?: boolean;
      hideFromSearch?: boolean;
    };
    applyHostnameSearchVisibility(data?.hideFromSearch === true);
    const team = data?.team;
    if (!team || data.standardDefault || data.notFound || team.analyticsTrackingEnabled !== true) {
      setAnalyticsGate("allowed");
      return;
    }
    const config: AnalyticsPopupConfig = sanitizeAnalyticsPopupConfig(team.analyticsPopupConfig);
    const stored = readStoredAnalyticsConsent();
    const teamId = String(team._id || "");
    if (consentMatchesTeam(stored, teamId, config.policyVersion) && stored) {
      setAnalyticsGate(stored.status === "accepted" ? "allowed" : "denied");
      if (stored.status === "accepted") {
        syncAnalyticsConsent(stored).catch(() => undefined);
      }
      return;
    }
    setAnalyticsGate("denied");
  } catch {
    if (gate === "unknown") setAnalyticsGate("allowed");
  }
}
